import {
  getModelBlobUrl,
  isModelCacheSupported,
  putModel,
  touchModel,
} from 'Pods/ThreeDStudio/Services/modelCache'
import { isDesktopBreakpoint } from 'Pods/ThreeDStudio/Shared/Utils/isDesktopBreakpoint'

import { NOOP } from 'Utils/stableDefaults'
import { getBreakPoint } from 'Utils/useBreakpoint'

const SUPPORTED_FORMATS = new Set(['glb', 'gltf', 'fbx', 'stl', 'obj'])

// Read once at load time rather than through the hook: this runs inside the load
// chain, not a render, and the viewport will not change mid-load in any way that
// should retroactively decide whether to keep a copy.
const mayStoreCopy = () => (
  typeof window !== 'undefined' && isDesktopBreakpoint(getBreakPoint(window.innerWidth))
)

// A blob URL carries no file extension, so the viewer cannot sniff the format
// from it the way it does for an asset URL - the format has to be passed
// explicitly or the load is rejected.
const formatFromUrl = (url) => {
  try {
    const extension = new URL(url, window.location.href).pathname.split('.').pop().toLowerCase()

    return SUPPORTED_FORMATS.has(extension) ? extension : null
  } catch {
    return null
  }
}

// Downloads once and serves the load from that same body while a clone streams
// into storage. Deliberately not awaited: the copy is for later, and making the
// visible load wait on a disk write would tax every cold open to benefit the
// next one. Any failure falls back to letting the viewer fetch the URL itself.
const storeThenLoad = async (urlToLoad, format, resolvedFormat) => {
  const fallback = { source: urlToLoad, format, release: NOOP }

  try {
    const response = await fetch(urlToLoad)
    if (!response.ok) {
      return fallback
    }

    putModel(urlToLoad, response.clone())
    const blobUrl = URL.createObjectURL(await response.blob())

    return {
      source: blobUrl,
      format: resolvedFormat,
      release: () => URL.revokeObjectURL(blobUrl),
    }
  } catch {
    // Offline, CORS-blocked, or aborted: the viewer's own load reports the
    // failure with the context Sentry needs, so say nothing here.
    return fallback
  }
}

/**
 * Picks what to hand `viewer.loadModel`: a blob from Cache Storage when this
 * model is held, the bytes just downloaded when it is not, and the network URL
 * unchanged when neither is possible. Falls back to the
 * network on every uncertainty, so a missing, evicted or unreadable cache entry
 * costs nothing but the usual download.
 *
 * @param {string|string[]} urlToLoad - asset URL, or the multi-file array
 * @param {string|undefined} format - explicit format, set for uploaded models
 * @returns {Promise<{source: string|string[], format: string|undefined, release: Function}>}
 *   `release` MUST be called once the load settles; it revokes the blob URL,
 *   which otherwise keeps the whole model resident for the life of the document.
 */
export const resolveModelSource = async (urlToLoad, format) => {
  // Multi-file loads (OBJ + MTL + texture) need every part together under its
  // original filename, which a single cached blob cannot express.
  if (typeof urlToLoad !== 'string') {
    return { source: urlToLoad, format, release: NOOP }
  }

  // An uploaded model is already a local blob - there is nothing to cache and
  // nothing to gain.
  if (urlToLoad.startsWith('blob:')) {
    return { source: urlToLoad, format, release: NOOP }
  }

  const resolvedFormat = format ?? formatFromUrl(urlToLoad)
  if (!resolvedFormat) {
    return { source: urlToLoad, format, release: NOOP }
  }

  const blobUrl = await getModelBlobUrl(urlToLoad)
  if (blobUrl) {
    // Reopening a model counts as use, so eviction drops what has gone cold
    // rather than whatever happened to be stored first.
    touchModel(urlToLoad)

    return {
      source: blobUrl,
      format: resolvedFormat,
      release: () => URL.revokeObjectURL(blobUrl),
    }
  }

  // Nothing stored, so this load pays for the download either way - take the
  // bytes here and keep a copy, which is what makes any opened model available
  // on the way back to it. Skipped where the cache is unusable, and on phones and
  // small tablets, so both keep the plain loader path exactly as it was: no extra
  // buffering of a 60 MB body and nothing written to a device that can spare far
  // less storage than a desktop. A copy stored on a wider viewport is still read
  // back below md - that costs nothing and saves a download.
  if (!isModelCacheSupported() || !mayStoreCopy()) {
    return { source: urlToLoad, format, release: NOOP }
  }

  return storeThenLoad(urlToLoad, format, resolvedFormat)
}

export default resolveModelSource
